import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import Cookies from 'js-cookie';
import { createManagementTask } from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { TaskStatus } from './taskWorkflow';
import { unallocated } from './budget';
import {
    kindOptions,
    TaskKind,
    usesPermissionFlowKind,
    PERMISSION_END_BEFORE_START_MESSAGE,
    AUTHORITY_REQUIRED_MESSAGE,
    APPLICATION_NUMBER_REQUIRED_MESSAGE,
    permissionEndBeforeStart,
    missingPermissionAuthority,
    missingPermissionApplicationNumber
} from './permission';
import { useOwnerUserOptions } from './ownerOptions';
import LegalCaseFields from './LegalCaseFields';
import { legalCaseError, legalCaseFormValues, legalCasePayload } from './legalCase';
import { useLegalCaseOptions, useProjectUserOptions } from './legalCaseOptions';

export { TaskStatus, statusOptions } from './taskWorkflow';

// New tasks always start as Draft; the status moves on from the task page.
const draftStatusOption = [{ label: TaskStatus.DRAFT, value: TaskStatus.DRAFT }];

// `lockedProjectId` comes from the project page the modal was opened on — the
// project is already decided, so the picker is hidden and the id goes straight
// into the payload.
const CreateTaskModal = ({ onClose, projects, lockedProjectId = null }) => {
    const queryClient = useQueryClient();
    const { register, handleSubmit, watch } = useForm({
        defaultValues: {
            title: '',
            description: '',
            owner_id: Cookies.get('userId') || '',
            project_id: lockedProjectId ?? '',
            priority: 'Medium',
            budget: '',
            status: TaskStatus.DRAFT,
            dueDate: '',
            kind: TaskKind.GENERAL,
            authority: '',
            applicationNumber: '',
            permissionStartDate: '',
            permissionEndDate: '',
            legalCase: legalCaseFormValues(null)
        }
    });

    const projectId = lockedProjectId ?? watch('project_id');
    const ownerOptions = useOwnerUserOptions(projectId);
    const kind = watch('kind');
    const isLegalCase = kind === TaskKind.LEGAL_CASE;
    const legalOptions = useLegalCaseOptions({ enabled: isLegalCase });
    const projectPeople = useProjectUserOptions(isLegalCase ? projectId : null);

    const projectOptions = projects?.map(p => ({
        label: p.label,
        value: p.value
    })) || [];

    const priorityOptions = [
        { label: 'High', value: 'High' },
        { label: 'Medium', value: 'Medium' },
        { label: 'Low', value: 'Low' }
    ];

    const budget = watch('budget');
    const remaining = unallocated(budget, [], null);

    const createMutation = useMutation({
        mutationFn: createManagementTask,
        onSuccess: () => {
            toast.success('Task created successfully');
            queryClient.invalidateQueries(['managementTasks']);
            onClose();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to create task');
        }
    });

    const onSubmit = (data) => {
        const taskKind = data.kind || TaskKind.GENERAL;
        if (usesPermissionFlowKind(taskKind)) {
            if (missingPermissionAuthority(data.authority)) {
                toast.error(AUTHORITY_REQUIRED_MESSAGE);
                return;
            }
            if (missingPermissionApplicationNumber(data.applicationNumber)) {
                toast.error(APPLICATION_NUMBER_REQUIRED_MESSAGE);
                return;
            }
            if (permissionEndBeforeStart(data.permissionStartDate, data.permissionEndDate)) {
                toast.error(PERMISSION_END_BEFORE_START_MESSAGE);
                return;
            }
        }
        const legalCase = taskKind === TaskKind.LEGAL_CASE ? legalCasePayload(data.legalCase) : null;
        const legalError = legalCase && legalCaseError(legalCase);
        if (legalError) {
            toast.error(legalError);
            return;
        }
        // The field is not rendered when the project is locked, so take the id
        // from the prop rather than trusting it to survive handleSubmit.
        const effectiveProjectId = lockedProjectId ?? data.project_id;
        const payload = {
            title: data.title,
            description: data.description,
            owner_id: Number(data.owner_id),
            project_id: Number(effectiveProjectId),
            priority: data.priority,
            budget:
                taskKind === TaskKind.LEGAL_CASE || data.budget === '' || data.budget == null
                    ? null
                    : Number(data.budget),
            dueDate: data.dueDate,
            kind: taskKind
        };
        if (legalCase) {
            // A court case opens live at its case status, and the API refuses a
            // status sent alongside one.
            payload.legalCase = legalCase;
        } else {
            // Every other new task starts as Draft. The Status field is locked,
            // and react-hook-form drops disabled fields, so don't read it from data.
            payload.status = TaskStatus.DRAFT;
        }
        if (usesPermissionFlowKind(payload.kind)) {
            payload.authority = data.authority || null;
            payload.applicationNumber = data.applicationNumber || null;
            payload.permissionStartDate = data.permissionStartDate || null;
            payload.permissionEndDate = data.permissionEndDate || null;
        }

        createMutation.mutate(payload);
    };

    return (
        <Modal
            name="Create New Task"
            width={70}
            onClose={onClose}
            hideClose={false}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <FormInput
                        label="Title"
                        register={register}
                        register_key="title"
                        type="text"
                        isRequired={true}
                        width={100}
                    />
                    {!lockedProjectId && (
                        <FormInput
                            label="Project"
                            register={register}
                            register_key="project_id"
                            type="dropdown"
                            options={projectOptions}
                            isRequired={true}
                            width={100}
                        />
                    )}
                    <div className="col-span-2">
                        <FormInput
                            label="Description"
                            register={register}
                            register_key="description"
                            type="text"
                            isRequired={true}
                            width={100}
                        />
                    </div>

                    <FormInput
                        label="Owner"
                        register={register}
                        register_key="owner_id"
                        type="dropdown"
                        options={ownerOptions}
                        isRequired={true}
                        width={100}
                    />
                    <FormInput
                        label="Priority"
                        register={register}
                        register_key="priority"
                        type="dropdown"
                        options={priorityOptions}
                        isRequired={true}
                        width={100}
                    />
                    {!isLegalCase && (
                        <FormInput
                            label="Status"
                            register={register}
                            register_key="status"
                            type="dropdown"
                            options={draftStatusOption}
                            isRequired={true}
                            readOnly={true}
                            width={100}
                        />
                    )}
                    <FormInput
                        label="Type"
                        register={register}
                        register_key="kind"
                        type="dropdown"
                        options={kindOptions}
                        isRequired={true}
                        width={100}
                    />
                    {usesPermissionFlowKind(kind) && (
                        <>
                            <FormInput
                                label="Authority"
                                register={register}
                                register_key="authority"
                                type="text"
                                isRequired={true}
                                width={100}
                            />
                            <FormInput
                                label="Application no."
                                register={register}
                                register_key="applicationNumber"
                                type="text"
                                isRequired={true}
                                width={100}
                            />
                            <FormInput
                                label="Start Date"
                                register={register}
                                register_key="permissionStartDate"
                                type="date"
                                width={100}
                            />
                            <FormInput
                                label="End Date"
                                register={register}
                                register_key="permissionEndDate"
                                type="date"
                                width={100}
                            />
                        </>
                    )}
                    {/* A court case's expenses go straight to Accounts and never count
                        against a budget. */}
                    {!isLegalCase && (
                        <>
                            <FormInput
                                label="Budget (₹)"
                                register={register}
                                register_key="budget"
                                type="number"
                                width={100}
                            />
                            <p className="text-xs text-gray-500 col-span-2">
                                Unallocated: ₹{remaining.toLocaleString()}
                            </p>
                        </>
                    )}
                    <FormInput
                        label={isLegalCase ? 'Target date' : 'Due Date'}
                        register={register}
                        register_key="dueDate"
                        type="date"
                        isRequired={true}
                        width={100}
                    />
                    {isLegalCase && (
                        <p className="self-end text-xs text-gray-500">
                            When the next action is due. The case opens live at its case status, not as a draft.
                        </p>
                    )}
                    {isLegalCase &&
                        (legalOptions.loaded ? (
                            <LegalCaseFields
                                register={register}
                                watch={watch}
                                options={legalOptions}
                                userOptions={projectPeople}
                            />
                        ) : (
                            <p className="col-span-2 text-sm text-gray-500">Loading court case fields…</p>
                        ))}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="bg-[#346C75] text-white px-8 py-2 rounded hover:bg-[#2a565e]"
                    >
                        Create Task
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default CreateTaskModal;
